import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import BrandSpinner from "@/components/ui/BrandSpinner";
import { usePublicHelpHub } from "@/hooks/help-hub/useHelpHub";
import { useHelpHubPresentation } from "@/hooks/help-hub/useHelpHubPresentation";
import { SITE_URL } from "@/config/site";
import type { HelpHubDTO } from "@/types/help-hub/help-hub";
import { hubBrandTheme } from "./brandInk";
import { HubFooter, HubHeader, HubNotFound } from "./components/HubChrome";
import { HubAccessSection } from "./components/HubAccessSection";
import { HubAppSection } from "./components/HubAppSection";
import { HubContactSection } from "./components/HubContactSection";
import { HubCustomSections } from "./components/HubCustomSections";
import { HubDownloadsSection } from "./components/HubDownloadsSection";
import { HubFaqSection } from "./components/HubFaqSection";
import { HubGenieStage } from "./components/HubGenieStage";
import { HubGuidesSection } from "./components/HubGuidesSection";
import { HubHero } from "./components/HubHero";
import { HubNavigation } from "./components/HubNavigation";
import { HubTopicsSection } from "./components/HubTopicsSection";
import "./help-hub.css";

/** Link previews and structured data need a real link, not an embedded logo. */
const isLinkUrl = (url: string | null | undefined): url is string => !!url && /^https?:\/\//.test(url);

/**
 * Structured data for a Genie Page that has opted into search.
 *
 * Deliberately narrow: a `WebSite` plus the business itself. There is no
 * `FAQPage` block, because this page publishes *questions* and lets the Genie
 * answer them live — putting answers into structured data that the page cannot
 * show would be describing content we don't have.
 */
const buildJsonLd = (hub: HelpHubDTO, displayName: string, canonical: string) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: `${displayName} — Help Centre`,
  url: canonical,
  ...(hub.description ? { description: hub.description } : {}),
  publisher: {
    "@type": "Organization",
    name: displayName,
    ...(isLinkUrl(hub.branding.logoUrl) ? { logo: hub.branding.logoUrl } : {}),
    ...(hub.phoneNumber || hub.emailAddress
      ? {
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer support",
          ...(hub.phoneNumber ? { telephone: hub.phoneNumber } : {}),
          ...(hub.emailAddress ? { email: hub.emailAddress } : {}),
        },
      }
      : {}),
  },
});

/**
 * A Genie's public front door at `/help/:url_name`.
 *
 * Published-content bands disappear when they have no data — so a
 * business that has done nothing but create a Genie still gets a page that
 * looks deliberate, and one that has published guides, manuals and a phone
 * number gets a full support centre without configuring anything.
 *
 * The page adds no capability. It is the Genie, the phone number, the
 * published guides, the manuals and an email address, arranged so a stranger
 * finds all of them at a glance. Everything a visitor does here lands in the
 * owner's Inbox exactly as it does today.
 */
const HelpHubPage = () => {
  const { url_name: urlName } = useParams<{ url_name: string }>();
  const { data: hub, isLoading } = usePublicHelpHub(urlName);
  const { stage, openWithQuestion, openStage, closeStage, channels } = useHelpHubPresentation(hub, urlName ?? "");

  if (isLoading) {
    return <BrandSpinner label="Opening the help centre…" />;
  }

  if (!hub) {
    return <HubNotFound />;
  }

  const { branding } = hub;
  const theme = hubBrandTheme(branding);
  const displayName = branding.brandName || hub.name;
  const pageTitle = `${displayName} — Help Centre`;
  const canonical = `${SITE_URL}/help/${encodeURIComponent(hub.urlName)}`;
  const description =
    hub.subheadline ||
    hub.description ||
    `Get help from ${displayName} — ask a question, call, or read the guides.`;

  return (
    <div className="genie-support-page flex min-h-[100dvh] flex-col" style={{ ["--hub-primary" as string]: theme.primary }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={description} />
        {/* Opt-in per page, and the server only reports it once the hub has
            enough published guides to be worth landing on. */}
        <meta
          name="robots"
          content={hub.searchEngineVisible ? "index,follow" : "noindex"}
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        {isLinkUrl(branding.logoUrl) && <meta property="og:image" content={branding.logoUrl} />}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={description} />
        {hub.searchEngineVisible && (
          <script type="application/ld+json">
            {JSON.stringify(buildJsonLd(hub, displayName, canonical))}
          </script>
        )}
      </Helmet>

      <main className="flex-1">
        {/* The header carries the customer's crest — logo included — so the
            hero below stays clean: headline, ask box, channels. */}
        <HubHeader name={hub.name} branding={branding} wide />
        <HubNavigation hub={hub} />
        <HubHero
          name={displayName}
          headline={hub.headline || "A question?\nLet’s work\nthrough it."}
          subheadline={hub.subheadline || hub.welcomeMessage || hub.description}
          askPlaceholder={hub.askPlaceholder || "Ask anything — in your own words"}
          branding={branding}
          channels={channels}
          questions={hub.questions}
          onAsk={openWithQuestion}
          onTalk={openStage}
        />

        <div className="hub-page-width hub-page-sections">
          <HubGuidesSection
            key={`guides-${hub.genieId}`}
            topics={hub.topics}
            guideCount={hub.guideCount}
            branding={branding}
            urlName={hub.urlName}
            onAsk={openWithQuestion}
          />

          <HubTopicsSection key={`topics-${hub.genieId}`} topics={hub.topics} branding={branding} urlName={hub.urlName} onAsk={openWithQuestion} />

          <HubFaqSection questions={hub.questions} branding={branding} onAsk={openWithQuestion} />

          <HubDownloadsSection downloads={hub.downloads} branding={branding} />

          <HubCustomSections sections={hub.customSections} branding={branding} />

          <div className="hub-contact-grid">
            <HubAccessSection hub={hub} displayName={displayName} onTalk={openStage} />
            {hub.showApp && <HubAppSection hub={hub} displayName={displayName} />}
          </div>

          {(hub.contact?.hours || hub.contact?.address || hub.links.length > 0) && <HubContactSection hub={hub} />}
        </div>
      </main>

      <HubFooter name={displayName} branding={branding} wide />

      {stage && (
        <HubGenieStage
          key={hub.genieId}
          title={displayName}
          branding={branding}
          question={stage.question}
          onClose={closeStage}
        />
      )}
    </div>
  );
};

export default HelpHubPage;
